import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import {
  useCreateEnrollment,
  useSearchStudentByPhone,
} from '@/hooks/mutations/useEnrollmentMutations'
import {
  calcValidTill,
  COMBO_TYPES,
  getCourseLabel,
  getProgramLabel,
  normalizeCheckoutPhone,
  normalizeDialCode,
  PACKAGES,
  PAYMENT_MODES,
  today,
} from '@fintree/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

import { useActor } from '@/hooks/useActor'
import { useCourseAttempts } from '@/hooks/useCourseAttempts'
import { COMMON_COUNTRIES, CITIES_DATALIST, STATES_DATALIST } from '@/lib/countries'
import { InstallmentPlanFields } from '@/components/enroll/InstallmentPlanFields'
import { COURSE_GROUPS, enrollmentFormModel, isNoAttemptNoPackageCourse, resolveCourseKeyForAttempts } from './enrollConstants'
import type { InternationalFormInput, InstallmentFormEntry } from './enrollConstants'
import { ChevronDownIcon, SearchIcon } from 'lucide-react'

export function InternationalEnrollForm() {
  const navigate = useNavigate()
  const actor = useActor()
  const [countryOpen, setCountryOpen] = useState(false)
  const [countrySearch, setCountrySearch] = useState('')
  const [duplicateInfo, setDuplicateInfo] = useState<{
    name: string | null
    course: string | null
    attempt: string | null
    validTill: string | null
    historyCount: number
    activeEnrollments: { course: string | null; attempt: string | null; program: string | null }[]
  } | null>(null)

  const form = useForm<InternationalFormInput>({
    resolver: zodResolver(enrollmentFormModel.international),
    defaultValues: {
      name: '',
      countryCode: '+971',
      phoneNumber: '',
      country: '',
      email: '',
      city: '',
      state: '',
      course: '',
      comboType: '',
      package: '',
      attempt: '',
      customAttempt: '',
      enrollmentDate: today(),
      amount: undefined,
      currency: 'USD',
      paymentMode: '',
      razorpayPaymentId: '',
      installments: [],
    },
  })

  /* eslint-disable react-hooks/incompatible-library -- RHF watch(); React Compiler false positive; derived enroll UI state */
  const selectedCountry = form.watch('country')
  const selectedCourse = form.watch('course')
  const selectedCombo = form.watch('comboType')
  const selectedAttempt = form.watch('attempt')
  const selectedCustomAttempt = form.watch('customAttempt')
  const selectedDate = form.watch('enrollmentDate')
  const revisionMonths = form.watch('revisionMonths')
  const selectedPaymentMode = form.watch('paymentMode')
  /* eslint-enable react-hooks/incompatible-library */
  const isRevisionCourse = selectedCourse.startsWith('REV_') || selectedCourse === 'REV_PACK'
  const isNoAttemptNoPackage = isNoAttemptNoPackageCourse(selectedCourse)

  const resolvedCourse = useMemo(() => {
    if (selectedCourse === 'COMBO' && selectedCombo) {
      return COMBO_TYPES[selectedCombo as keyof typeof COMBO_TYPES]?.startCourse ?? 'CFA_L1'
    }
    return selectedCourse
  }, [selectedCombo, selectedCourse])

  const courseKeyForAttempts = useMemo(
    () => resolveCourseKeyForAttempts(selectedCourse, selectedCombo),
    [selectedCourse, selectedCombo]
  )

  const { attemptOptions, isLoading: attemptsLoading } = useCourseAttempts(courseKeyForAttempts)
  const comboNeedsType = selectedCourse === 'COMBO' && !selectedCombo

  useEffect(() => {
    if (isNoAttemptNoPackage && !revisionMonths) {
      form.setValue('revisionMonths', 3)
    }
  }, [form, isNoAttemptNoPackage, revisionMonths])

  const resolvedAttempt = selectedAttempt === '__custom__' ? selectedCustomAttempt : selectedAttempt
  const resolvedProgram = isRevisionCourse || isNoAttemptNoPackage ? null : (form.watch('package') || null)
  const duplicateConflict = duplicateInfo?.activeEnrollments.some(
    (e) =>
      e.course === resolvedCourse &&
      (e.attempt || '') === (resolvedAttempt || '') &&
      (e.program || '') === (resolvedProgram || '')
  )
  const validTillPreview = calcValidTill(
    resolvedCourse,
    resolvedAttempt,
    selectedCombo || undefined,
    selectedDate,
    isNoAttemptNoPackage ? (revisionMonths ?? 3) : revisionMonths
  )

  const filteredCountries = useMemo(() => {
    const term = countrySearch.trim().toLowerCase()
    if (!term) return COMMON_COUNTRIES
    return COMMON_COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(term) ||
        c.dialCode.includes(term) ||
        c.code.toLowerCase().includes(term)
    )
  }, [countrySearch])

  const duplicateMutation = useSearchStudentByPhone({
    onSuccess: (result) => {
      if (!result.student) {
        setDuplicateInfo(null)
        return
      }
      setDuplicateInfo({
        name: result.student.name,
        course: result.student.course,
        attempt: result.student.attempt,
        validTill: result.student.valid_till,
        historyCount: result.enrollmentHistoryCount,
        activeEnrollments: result.activeEnrollments,
      })
    },
  })

  const handlePhoneBlur = () => {
    const countryCode = form.getValues('countryCode')
    const phoneNumber = form.getValues('phoneNumber')
    const country = form.getValues('country')
    const currency = form.getValues('currency')
    if (!phoneNumber) return
    const normalized = normalizeCheckoutPhone({
      rawPhone: phoneNumber,
      addressCountry: country,
      addressCountryCode: countryCode,
      currency,
    })
    const fullPhone = normalized.ok
      ? normalized.phone
      : `${normalizeDialCode(countryCode)}${phoneNumber.replace(/\D/g, '')}`
    duplicateMutation.mutate(fullPhone)
  }

  const mutation = useCreateEnrollment(actor, {
    onSuccess: (_, payload) => {
      navigate(`/students/${encodeURIComponent(payload.phone)}`)
    },
  })

  return (
    <form
      className="space-y-4 rounded-lg border border-border bg-card p-6"
      onSubmit={form.handleSubmit((values) => {
        const normalized = normalizeCheckoutPhone({
          rawPhone: values.phoneNumber,
          addressCountry: values.country,
          addressCountryCode: values.countryCode,
          currency: values.currency,
        })
        if (!normalized.ok) {
          toast.error('Invalid phone number for selected country')
          return
        }
        mutation.mutate({
          name: values.name,
          phone: normalized.phone,
          countryCode: normalized.countryCode ?? undefined,
          email: values.email || undefined,
          city: values.city || undefined,
          country: values.country,
          course: values.course === 'COMBO' ? 'CFA_L1' : values.course,
          comboType: values.course === 'COMBO' ? (values.comboType as keyof typeof COMBO_TYPES) : undefined,
          revisionMonths:
            values.course.startsWith('REV_') || values.course === 'REV_PACK' || isNoAttemptNoPackageCourse(values.course)
              ? isNoAttemptNoPackageCourse(values.course)
                ? (values.revisionMonths ?? 3)
                : values.revisionMonths
              : undefined,
          package:
            values.course.startsWith('REV_') || values.course === 'REV_PACK' || isNoAttemptNoPackageCourse(values.course)
              ? undefined
              : values.package,
          attempt:
            values.course === 'FM' || isNoAttemptNoPackageCourse(values.course)
              ? undefined
              : values.attempt === '__custom__'
                ? values.customAttempt
                : values.attempt,
          enrollmentDate: values.enrollmentDate,
          amount: values.amount,
          currency: values.currency,
          paymentMode: values.paymentMode || undefined,
          razorpayPaymentId: values.razorpayPaymentId || undefined,
          source: 'manual_international',
          installments: values.installments.length
            ? values.installments.map((inst: InstallmentFormEntry) => ({
                amount: inst.amount,
                dueDate: inst.dueDate,
                notes: inst.notes || null,
                phone: normalized.phone,
              }))
            : undefined,
        })
      })}
    >
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-3">
          <div>
            <Label>Full Name</Label>
            <Input disabled={mutation.isPending} {...form.register('name')} />
          </div>

          <div>
            <Label>Country</Label>
            <Popover open={countryOpen} onOpenChange={setCountryOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  disabled={mutation.isPending}
                  className="flex h-8 w-full items-center justify-between gap-2 rounded-lg border border-input bg-transparent px-2.5 py-2 text-left text-sm transition-colors outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <span className={selectedCountry ? 'text-foreground' : 'text-muted-foreground'}>
                    {selectedCountry || 'Select country'}
                  </span>
                  <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground" strokeWidth={1.5} />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[220px] bg-card p-0" align="start">
                <div className="flex items-center gap-2 border-b border-border bg-card px-2 py-1.5">
                  <SearchIcon className="size-4 text-muted-foreground" strokeWidth={1.5} />
                  <input
                    type="text"
                    placeholder="Search countries..."
                    value={countrySearch}
                    onChange={(e) => setCountrySearch(e.target.value)}
                    className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                    autoFocus
                  />
                </div>
                <div className="max-h-64 overflow-y-auto bg-card">
                  <div className="p-1 bg-card">
                    {filteredCountries.length === 0 ? (
                      <div className="px-2 py-3 text-sm text-muted-foreground">No countries found</div>
                    ) : (
                      filteredCountries.map((c) => (
                        <button
                          key={c.code}
                          type="button"
                          className="flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted"
                          onClick={() => {
                            form.setValue('country', c.name, { shouldValidate: true })
                            form.setValue('countryCode', c.dialCode, { shouldValidate: true })
                            setCountryOpen(false)
                            setCountrySearch('')
                          }}
                        >
                          <span>{c.name}</span>
                          <span className="text-xs text-muted-foreground">{c.dialCode}</span>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              </PopoverContent>
            </Popover>
            {form.formState.errors.country ? (
              <p className="text-xs text-destructive">{form.formState.errors.country.message}</p>
            ) : null}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Country Code</Label>
              <Input disabled readOnly {...form.register('countryCode')} className="bg-muted" />
            </div>
            <div>
              <Label>Phone Number</Label>
              <Input
                disabled={mutation.isPending}
                {...form.register('phoneNumber')}
                onBlur={(e) => {
                  form.register('phoneNumber').onBlur(e)
                  handlePhoneBlur()
                }}
              />
            </div>
          </div>

          <div>
            <Label>Email</Label>
            <Input
              disabled={mutation.isPending}
              {...form.register('email', {
                onChange: () => {
                  mutation.reset()
                },
              })}
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>City</Label>
              <Input disabled={mutation.isPending} {...form.register('city')} list="cities-datalist" />
              <datalist id="cities-datalist">
                {CITIES_DATALIST.map((city) => (
                  <option key={city} value={city} />
                ))}
              </datalist>
            </div>
            <div>
              <Label>State / Province</Label>
              <Input disabled={mutation.isPending} {...form.register('state')} list="states-datalist" />
              <datalist id="states-datalist">
                {STATES_DATALIST.map((state) => (
                  <option key={state} value={state} />
                ))}
              </datalist>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <Label>Course</Label>
            <select
              className="h-8 w-full rounded-lg border border-input bg-background px-2 text-sm"
              disabled={mutation.isPending}
              {...form.register('course')}
            >
              <option value="">Select course</option>
              {COURSE_GROUPS.map((group) => (
                <optgroup key={group.label} label={group.label}>
                  {group.options.map((option) => (
                    <option key={option} value={option}>
                      {getCourseLabel(option)}
                    </option>
                  ))}
                </optgroup>
              ))}
              <optgroup label="Combo">
                <option value="COMBO">Combo Package</option>
              </optgroup>
            </select>
          </div>
          {selectedCourse === 'COMBO' ? (
            <div>
              <Label>Combo Type</Label>
              <select
                className="h-8 w-full rounded-lg border border-input bg-background px-2 text-sm"
                disabled={mutation.isPending}
                {...form.register('comboType')}
              >
                <option value="">Select combo</option>
                <option value="L1_L2">CFA L1 + L2</option>
                <option value="L2_L3">CFA L2 + L3</option>
                <option value="L1_L2_L3">CFA L1 + L2 + L3</option>
              </select>
            </div>
          ) : null}
          {isRevisionCourse ? (
            <div>
              <Label>Revision Duration</Label>
              <select
                className="h-8 w-full rounded-lg border border-input bg-background px-2 text-sm"
                disabled={mutation.isPending}
                value={form.watch('revisionMonths') ?? ''}
                onChange={(event) => form.setValue('revisionMonths', Number(event.target.value) || undefined)}
              >
                <option value="">Select</option>
                <option value="3">3 months</option>
                <option value="6">6 months</option>
              </select>
            </div>
          ) : null}
          {isNoAttemptNoPackage ? (
            <div>
              <Label>Valid Till Duration (months)</Label>
              <Input
                type="number"
                min={1}
                disabled={mutation.isPending}
                value={revisionMonths ?? 3}
                onChange={(event) => form.setValue('revisionMonths', Number(event.target.value) || 3)}
              />
            </div>
          ) : null}
          {!isRevisionCourse && !isNoAttemptNoPackage ? (
            <div>
              <Label>Package</Label>
              <select
                className="h-8 w-full rounded-lg border border-input bg-background px-2 text-sm"
                disabled={mutation.isPending}
                {...form.register('package')}
              >
                <option value="">Select package</option>
                {PACKAGES.map((pkg) => (
                  <option key={pkg} value={pkg}>
                    {getProgramLabel(pkg)}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          <div>
            <Label>Attempt</Label>
            <select
              className="h-8 w-full rounded-lg border border-input bg-background px-2 text-sm"
              disabled={mutation.isPending || selectedCourse === 'FM' || isNoAttemptNoPackage || comboNeedsType}
              {...form.register('attempt')}
            >
              <option value="">
                {selectedCourse === 'FM' || isNoAttemptNoPackage
                  ? 'Not required for this course'
                  : comboNeedsType
                    ? 'Select combo type first'
                    : attemptsLoading
                      ? 'Loading attempts…'
                      : 'Select attempt'}
              </option>
              {attemptOptions.map((attempt) => (
                <option key={attempt} value={attempt}>
                  {attempt}
                </option>
              ))}
              {selectedCourse && selectedCourse !== 'FM' && !isNoAttemptNoPackage && !comboNeedsType ? (
                <option value="__custom__">+ Add new attempt</option>
              ) : null}
            </select>
          </div>
          {selectedAttempt === '__custom__' && !isNoAttemptNoPackage ? (
            <div>
              <Label>Custom Attempt</Label>
              <Input
                disabled={mutation.isPending}
                {...form.register('customAttempt')}
                placeholder="e.g. May_28"
              />
            </div>
          ) : null}
          <div>
            <Label>Enrollment Date</Label>
            <Input type="date" disabled={mutation.isPending} {...form.register('enrollmentDate')} />
          </div>
          <div>
            <Label>Currency</Label>
            <select className="h-8 w-full rounded-lg border border-input bg-background px-2 text-sm" disabled={mutation.isPending} {...form.register('currency')}>
              <option value="USD">USD</option>
              <option value="INR">INR</option>
            </select>
          </div>
          <div>
            <Label>Amount Paid</Label>
            <Input type="text" inputMode="numeric" disabled={mutation.isPending} {...form.register('amount')} />
            {form.formState.errors.amount ? <p className="text-xs text-destructive">{form.formState.errors.amount.message}</p> : null}
          </div>
          <div>
            <Label>Payment Mode</Label>
            <select
              className="h-8 w-full rounded-lg border border-input bg-background px-2 text-sm"
              disabled={mutation.isPending}
              {...form.register('paymentMode')}
            >
              <option value="">Select mode</option>
              {PAYMENT_MODES.map((mode) => (
                <option key={mode} value={mode}>
                  {mode}
                </option>
              ))}
            </select>
            {form.formState.errors.paymentMode ? (
              <p className="text-xs text-destructive">{form.formState.errors.paymentMode.message}</p>
            ) : null}
          </div>
          {selectedPaymentMode === 'Razorpay' ? (
            <div>
              <Label>Razorpay Payment ID (optional)</Label>
              <Input
                disabled={mutation.isPending}
                placeholder="pay_xxxxxxxxxxxxxxxx"
                {...form.register('razorpayPaymentId')}
              />
              {form.formState.errors.razorpayPaymentId ? (
                <p className="text-xs text-destructive">{form.formState.errors.razorpayPaymentId.message}</p>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      <InstallmentPlanFields
        values={form.watch('installments') ?? []}
        onChange={(values) => form.setValue('installments', values, { shouldValidate: true })}
        disabled={mutation.isPending}
      />

      {duplicateInfo ? (
        <div className={`rounded-lg border p-3 text-sm ${duplicateConflict ? 'border-red-300 bg-destructive/10 text-destructive' : 'border-fintree-amber bg-fintree-amber/10 text-foreground'}`}>
          {duplicateConflict ? (
            <>
              <p className="font-medium">This student already has an active enrollment for this course, attempt, and package.</p>
              <p>Submitting will fail. Use the Enrollment Change tab on the student profile to extend validity, retake, or upgrade package.</p>
            </>
          ) : (
            <>
              <p className="font-medium">This student already exists. Submitting will add a new enrollment record.</p>
              <p>
                Existing: {duplicateInfo.name ?? '—'} | {duplicateInfo.course ? getCourseLabel(duplicateInfo.course) : '—'} |{' '}
                {duplicateInfo.attempt ?? '—'} | Valid till: {duplicateInfo.validTill ?? '—'} | History count:{' '}
                {duplicateInfo.historyCount}
              </p>
            </>
          )}
        </div>
      ) : null}

      <div className="rounded-lg border border-border bg-background p-3 text-sm">
        <span className="text-muted-foreground">Valid Till Preview:</span>{' '}
        {validTillPreview ?? 'Not enough information'}
      </div>
      <Button type="submit" disabled={mutation.isPending}>
        {mutation.isPending ? 'Saving...' : 'Save enrollment'}
      </Button>
    </form>
  )
}
